// RequestKit.jsx - The form the whole business runs on. Every other page exists
// to get somebody here, so this file is optimised for completion above all else.
//
// The decisions behind it, because they are easy to undo by accident:
//
//   1. Screen one asks for nothing. It is five tiles, no typing, and one of them
//      is "not sure", so there is no wrong answer and no reason to hesitate. A
//      form that opens with an empty text box asking a nervous person to describe
//      their late mother's jewellery is the single biggest thing we can get wrong.
//   2. The item detail form is behind a button and says twice that it is optional.
//   3. Contact comes second, address third. Address is the longest screen, so it
//      goes last, when somebody has already invested two screens. It is also why
//      an abandoned form still leaves us a name and an email.
//   4. Everything is saved to the browser as you type. Closing the tab, hitting
//      back, or losing signal does not cost you the form.
//   5. Validation runs when you leave a field. Never per keystroke, never only on
//      submit.
//   6. The action button is always reachable: inline on desktop, pinned to the
//      bottom of the screen on a phone.
//
// LEGEND
//   1. CHIPS / PURITY      the vocabularies screen one offers
//   2. Progress            the step bar
//   3. useDraft            save and restore the whole form
//   4. StepWhat            one tap answers, optional detail behind a button
//   5. StepContact         name, email, phone, consent
//   6. StepAddress         address, with the ZIP lookup
//  6b. Agreement           the tick box that makes the agreement provable
//   7. PROMISES/TrustRow   the objections, answered where they are felt
//   9. RequestKit          the page

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import clsx from "clsx";

import { Seo, JsonLd, breadcrumbSchema } from "../lib/seo.jsx";
import { useForm } from "../lib/hooks.js";
import { publicApi } from "../lib/api.js";
import { remember } from "../lib/session.js";
import * as analytics from "../lib/analytics.js";
import { referralCode, forgetReferral, cardSerial, forgetCard } from "../lib/referral.js";
import { US_STATES, METAL_LABELS, day } from "../lib/format.js";
import { CONSENT, requiredAt } from "../lib/legal.js";
import { Button, Input, Select, Checkbox, Alert, Modal } from "../ui/kit.jsx";
import { AddressField } from "../ui/address.jsx";
import { Enter, Reveal } from "../ui/motion.jsx";
import { ChainCoil, CoinStack, RingSolitaire, BarStack, Loupe } from "../ui/illustrations.jsx";
import { KIT } from "../lib/media.js";
import { Figure, WhatHappensNext } from "./parts.jsx";
import {
  ArrowRight, ArrowLeft, ArrowUpRight, Plus, Trash, Check, Shield, Truck, Clock,
  Dollar, CheckCircle, Phone, Video,
} from "../ui/icons.jsx";

////////// 1. VOCABULARIES //////////

const CHIPS = [
  { value: "gold", label: "Gold", hint: "Jewellery, chains, rings", art: ChainCoil },
  { value: "silver", label: "Silver", hint: "Flatware, .925, coins", art: CoinStack },
  { value: "platinum", label: "Platinum", hint: "Settings, bands, scrap", art: RingSolitaire },
  { value: "bullion", label: "Coins or bars", hint: "Bullion and rounds", art: BarStack },
  { value: "unsure", label: "Not sure yet", hint: "Send it, we will tell you", art: Loupe },
];

const PURITY_OPTIONS = {
  gold: [
    { value: "", label: "Not sure" },
    { value: "10k", label: "10K (marked 417)" },
    { value: "14k", label: "14K (marked 585)" },
    { value: "18k", label: "18K (marked 750)" },
    { value: "22k", label: "22K (marked 916)" },
    { value: "24k", label: "24K (marked 999)" },
  ],
  silver: [
    { value: "", label: "Not sure" },
    { value: "925", label: "Sterling (marked 925)" },
    { value: "900", label: "Coin silver (marked 900)" },
    { value: "999", label: "Fine silver (marked 999)" },
  ],
  platinum: [{ value: "", label: "Not sure" }, { value: "950", label: "PLAT / 950" }, { value: "900", label: "900" }],
  palladium: [{ value: "", label: "Not sure" }, { value: "950", label: "950" }],
  other: [{ value: "", label: "Not sure" }],
};

const blankItem = () => ({ description: "", metal: "gold", karat: "", quantity: 1, approxWeight: "", unit: "g" });

const STEP_LABELS = ["What you have", "How to reach you", "Where to send it"];
// Somebody holding a mailer is not waiting for one, so the last screen is asking
// where their money goes rather than where the envelope goes.
const MAILER_STEP_LABELS = ["What you have", "How to reach you", "Your address"];

////////// 2. PROGRESS //////////
// The bar never starts empty. Screen one counts as progress the moment it is on
// screen, because it is, and an empty bar reads as "this is going to take a while".

function Progress({ step }) {
  return (
    <div className="mb-5 sm:mb-6">
      <div className="flex items-baseline justify-between gap-4 mb-2.5">
        <p className="text-[13px] font-semibold text-ink">
          Step {step + 1} of {STEP_LABELS.length}
          <span className="text-muted font-normal"> &middot; {STEP_LABELS[step]}</span>
        </p>
        <p className="text-[12.5px] text-faint whitespace-nowrap">
          <Clock size={12} className="inline -mt-0.5 mr-1" />
          about 60 seconds
        </p>
      </div>
      <div className="h-1 bg-surface-2 overflow-hidden rounded-full">
        <div
          className="h-full bg-bronze rounded-full transition-[width] duration-500 ease-out"
          style={{ width: `${((step + 1) / STEP_LABELS.length) * 100}%` }}
        />
      </div>
    </div>
  );
}

////////// 3. DRAFT //////////
// Mirrored into this browser as you type, and cleared the moment the request
// succeeds. It never leaves the device.

const DRAFT_KEY = "igs.kit.draft.v1";
const DRAFT_MAX_AGE = 30 * 24 * 3600_000;

// One id per attempt at the form, so ten minutes of editing is one row on the
// server rather than forty. Regenerated only when a draft is cleared.
const newDraftId = () =>
  (globalThis.crypto?.randomUUID?.() || `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`);

function readDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.at || Date.now() - parsed.at > DRAFT_MAX_AGE) return null;
    return parsed;
  } catch {
    return null;
  }
}

// Write only. The draft is read once, by readDraft() into a ref, so there is no
// React state here to keep in step with it.
//
// It used to hold the draft in useLocal, and that was an infinite render loop:
// `payload` is a fresh object on every render, so the effect ran on every
// render, called setDraft with another new object, and set off the next render.
// React caps that with "Maximum update depth exceeded" and stops committing,
// which is why the form went on working while the navigation after a successful
// submit never rendered. The url changed and the page did not.
//
// So the effect keys on the serialised draft rather than the object holding it.
// Same content, same string, no effect, no write.
function useDraft(payload) {
  const first = useRef(true);
  const snapshot = JSON.stringify(payload);

  useEffect(() => {
    // Skip the first render, or an empty form would overwrite a real draft
    // before the restore has been applied.
    if (first.current) { first.current = false; return; }
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...JSON.parse(snapshot), at: Date.now() }));
    } catch { /* storage is off, or the draft is over quota. Neither is fatal. */ }
  }, [snapshot]);

  return useCallback(() => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* not available */ }
  }, []);
}

// Anything the campaign put in the URL, so a bounced form can be attributed.
function readUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    return Object.fromEntries(
      ["utm_source", "utm_medium", "utm_campaign"]
        .map((key) => [key, params.get(key)])
        .filter(([, value]) => value)
    );
  } catch {
    return {};
  }
}

////////// 4. SCREEN ONE //////////

function ItemRows({ items, onChange }) {
  const update = (index, patch) => onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="panel p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <p className="eyebrow pt-1">Item {index + 1}</p>
            <button
              type="button"
              onClick={() => onChange(items.filter((_, i) => i !== index))}
              className="btn btn-ghost btn-sm -mr-2 -mt-1"
              aria-label={`Remove item ${index + 1}`}
            >
              <Trash size={15} /> Remove
            </button>
          </div>

          <Input
            label="What is it"
            placeholder="Two gold chains, one broken"
            value={item.description}
            onChange={(event) => update(index, { description: event.target.value })}
          />

          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <Select
              label="Metal"
              value={item.metal}
              onChange={(event) => update(index, { metal: event.target.value, karat: "" })}
              options={Object.entries(METAL_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Select
              label="Marking, if you can see one"
              value={item.karat}
              onChange={(event) => update(index, { karat: event.target.value })}
              options={PURITY_OPTIONS[item.metal] || PURITY_OPTIONS.other}
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
            <Input
              label="How many"
              type="number" min="1" inputMode="numeric"
              value={item.quantity}
              onChange={(event) => update(index, { quantity: event.target.value })}
            />
            <Input
              label="Rough weight"
              type="number" step="0.1" min="0" inputMode="decimal" placeholder="Optional"
              value={item.approxWeight}
              onChange={(event) => update(index, { approxWeight: event.target.value })}
            />
            <Select
              label="Unit"
              wrapClassName="col-span-2 sm:col-span-1"
              value={item.unit}
              onChange={(event) => update(index, { unit: event.target.value })}
              options={[
                { value: "g", label: "grams" },
                { value: "dwt", label: "pennyweight" },
                { value: "ozt", label: "troy oz" },
                { value: "lb", label: "pounds" },
              ]}
            />
          </div>
        </div>
      ))}

      <button type="button" onClick={() => onChange([...items, blankItem()])} className="btn btn-quiet w-full">
        <Plus size={16} /> Add another item
      </button>
    </div>
  );
}

function StepWhat({ chips, setChips, items, setItems, onBegin, formName }) {
  const [showItems, setShowItems] = useState(items.length > 0);

  // Functional update, not a read of `chips`. Two taps in the same tick both read
  // the same stale array otherwise, and the first selection is silently lost.
  const toggle = (value) => {
    onBegin();
    setChips((current) =>
      current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value]);
  };

  return (
    <div>
      <fieldset>
        <legend className="sr-only">What are you thinking of selling</legend>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {CHIPS.map(({ value, label, hint, art: Drawing }) => {
            const active = chips.includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggle(value)}
                aria-pressed={active}
                className={clsx(
                  "relative text-left p-3 border rounded-xs transition-all duration-200",
                  active
                    ? "border-bronze bg-bronze-wash shadow-[inset_0_0_0_1px_var(--color-bronze)]"
                    : "border-rule bg-card hover:border-faint hover:-translate-y-0.5"
                )}
              >
                {active && (
                  <span className="absolute top-2 right-2 w-[18px] h-[18px] rounded-full bg-bronze text-white flex items-center justify-center">
                    <Check size={12} />
                  </span>
                )}
                <Drawing className="w-10 sm:w-11 -ml-1 -mt-0.5" />
                <span className="block text-[14.5px] font-semibold text-ink">{label}</span>
                <span className="block text-[12px] text-muted leading-snug mt-0.5">{hint}</span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <p className="hint mt-3.5 leading-relaxed">
        Tap any that apply, or none at all. You can change your mind about what to send right up
        until you post the kit.
      </p>

      {/* Behind a button on purpose. Opening on an empty text field is what makes
          people close the tab. */}
      <div className="mt-5 pt-5 border-t border-rule-soft">
        {!showItems ? (
          <p className="hint leading-relaxed">
            <button
              type="button"
              onClick={() => {
                onBegin();
                analytics.trackFormItems(formName);
                setItems([blankItem()]);
                setShowItems(true);
              }}
              className="inline-flex items-center gap-1.5 font-semibold text-ink underline-grow align-middle"
            >
              <Plus size={15} /> Add details about a piece
            </button>
            {" "}Optional, and most people skip it. It only helps us pick the right insurance
            level before the kit goes out.
          </p>
        ) : (
          <>
            <div className="flex items-baseline justify-between gap-4 mb-3">
              <p className="eyebrow">Details, optional</p>
              <button
                type="button"
                onClick={() => { setItems([]); setShowItems(false); }}
                className="text-[13px] text-muted hover:text-ink underline-grow"
              >
                Skip this
              </button>
            </div>
            <ItemRows items={items} onChange={setItems} />
          </>
        )}
      </div>
    </div>
  );
}

////////// 5. SCREEN TWO //////////

function StepContact({ form, touch }) {
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Input label="First name" required autoComplete="given-name" {...form.field("firstName")} onBlur={() => touch("firstName")} />
        <Input label="Last name" required autoComplete="family-name" {...form.field("lastName")} onBlur={() => touch("lastName")} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Input
          label="Email"
          type="email"
          required
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
          {...form.field("email")}
          onBlur={() => touch("email")}
        />
        <Input
          label="Mobile number"
          type="tel"
          required
          inputMode="tel"
          autoComplete="tel"
          placeholder="(401) 315-4653"
          {...form.field("phone")}
          onBlur={() => touch("phone")}
        />
      </div>

      <p className="hint leading-relaxed">
        Used for your own order and nothing else. Never sold, never shared, and no marketing calls.
      </p>

      <div className="panel p-3.5 space-y-3">
        {/* The wording is CONSENT.sms, and the server records that same string
            against the order. A consent record that does not say what was asked
            is not worth keeping, so the two are deliberately one string. */}
        <Checkbox
          label={CONSENT.sms}
          hint="A text when it ships, when it lands, and when your offer is ready. Only about your own order, never marketing."
          checked={form.values.smsConsent}
          onChange={(event) => form.set("smsConsent", event.target.checked)}
        />
        <Checkbox
          label="Email me when metal prices move sharply, a few times a year"
          checked={form.values.marketingConsent}
          onChange={(event) => form.set("marketingConsent", event.target.checked)}
        />
      </div>
    </div>
  );
}

////////// 6. SCREEN THREE //////////
// The ZIP lookup uses a free public postal API. If it is unavailable the fields
// stay editable and nothing breaks, which is the only acceptable behaviour for a
// convenience feature.

function useZipLookup(zip, onResolve) {
  const lastLookup = useRef("");

  useEffect(() => {
    const clean = String(zip || "").trim().slice(0, 5);
    if (clean.length !== 5 || !/^\d{5}$/.test(clean) || lastLookup.current === clean) return undefined;
    lastLookup.current = clean;

    const controller = new AbortController();
    fetch(`https://api.zippopotam.us/us/${clean}`, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const place = data?.places?.[0];
        if (place) onResolve({ city: place["place name"], state: place["state abbreviation"], zip: clean });
      })
      .catch(() => { /* offline or blocked, the fields stay manual */ });

    return () => controller.abort();
  }, [zip, onResolve]);
}

function StepAddress({ form, touch }) {
  // A ZIP that a picked address already filled. The ZIP lookup and Google
  // disagree on a real class of address: the post office files a township under
  // the name of the nearest town, and Google names the township. Whichever one
  // answered second used to win, which is how a correct city got overwritten
  // with a nearly correct one. The place wins, because it knows the building.
  const fromPlace = useRef("");

  const onResolve = useCallback(({ city, state, zip }) => {
    if (fromPlace.current === zip) return;
    form.set("address.city", city);
    form.set("address.state", state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useZipLookup(form.values.address?.postal, onResolve);

  // One pick fills the lot. Somebody who does not know their ZIP never has to.
  const onPick = (address) => {
    fromPlace.current = address.postal || "";
    for (const key of ["line1", "line2", "city", "state", "postal"]) {
      if (address[key]) form.set(`address.${key}`, address[key]);
    }
  };

  return (
    <div className="space-y-4 lg:space-y-3.5">
      {/* The street first, because it is now the field that fills the other
          three. It used to be last, under a ZIP box, which is the wrong way
          round for the person this screen is hardest for: somebody who is not
          sure of their address is asked for its most obscure part first and
          stops there. Same three rows either way, so the button below has not
          moved. */}
      <AddressField
        required
        label="Street address"
        placeholder="Start typing and pick yours from the list"
        value={form.values.address?.line1}
        onChange={(next) => form.set("address.line1", next)}
        onPick={onPick}
        onBlur={() => touch("address.line1")}
        error={form.errors["address.line1"]}
      />

      <Input label="Apartment, unit, or floor" autoComplete="address-line2" placeholder="Optional" {...form.field("address.line2")} />

      {/* Still typeable, and still filled by the ZIP on its own, for anybody
          whose address is too new to be listed or who prefers to type. */}
      <div className="grid grid-cols-2 sm:grid-cols-[150px_1fr_110px] gap-3 sm:gap-4">
        <Input
          label="ZIP code"
          required
          inputMode="numeric"
          maxLength={10}
          autoComplete="postal-code"
          {...form.field("address.postal")}
          onBlur={() => touch("address.postal")}
        />
        <Input label="City" required autoComplete="address-level2" wrapClassName="col-span-2 sm:col-span-1" {...form.field("address.city")} />
        <Select
          label="State"
          required
          placeholder="—"
          autoComplete="address-level1"
          options={US_STATES.map((code) => ({ value: code, label: code }))}
          {...form.field("address.state")}
        />
      </div>

      <div className="flex gap-3 p-3.5 panel-quiet text-[13px] text-muted leading-relaxed">
        <Truck size={17} className="text-bronze shrink-0 mt-0.5" />
        <p>
          A plain padded envelope. Nothing on the outside says what it is or who it is from.
        </p>
      </div>

      <Agreement form={form} touch={touch} />
    </div>
  );
}

////////// 6b. THE AGREEMENT //////////
// An explicit tick box, unticked to start with, immediately above the button
// that submits. Three things make this worth proving later: the box is a real
// choice rather than a notice, every document is a link that opens in a new tab
// so nobody has to lose the form to read one, and the version of each is printed
// beside it. The server stamps the version it actually recorded; these are what
// the reader saw.

function Agreement({ form, touch }) {
  const docs = requiredAt("request");

  return (
    <div className="panel p-4">
      <Checkbox
        label={CONSENT.request}
        checked={form.values.acceptedTerms}
        onChange={(event) => {
          form.set("acceptedTerms", event.target.checked);
          touch("acceptedTerms");
        }}
        error={form.errors.acceptedTerms}
      />

      <ul className="mt-3 pt-3 border-t border-rule-soft flex flex-wrap gap-x-5 gap-y-1">
        {docs.map((doc) => (
          <li key={doc.key}>
            <a
              href={`/legal/${doc.key}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[13px] font-semibold text-ink link"
            >
              {doc.title} <ArrowUpRight size={13} className="text-bronze" />
            </a>
            <span className="hint ml-1.5">v{doc.version}</span>
          </li>
        ))}
      </ul>

      <p className="hint mt-2.5 leading-snug">
        We record which version you accepted and when. Ask us and we will send you that record.
      </p>
    </div>
  );
}

////////// 7. REASSURANCE //////////

// One line each. The long version of all four is on /how-it-works, and a rail
// beside a form is a reminder rather than the argument.
const PROMISES = [
  { icon: Dollar, title: "The kit is free", body: "Postage both ways, and no fee if you decline." },
  { icon: Shield, title: "Insured both directions", body: "From the first carrier scan until it reaches us." },
  { icon: Video, title: "Opened on camera", body: "Seal to scale, on your tracking page the same day." },
  { icon: CheckCircle, title: "Declining costs nothing", body: "Packed as it arrived and shipped back at our expense." },
];

function TrustRow({ className }) {
  return (
    <ul className={clsx("grid xs:grid-cols-3 gap-2 sm:gap-2.5", className)}>
      {[[Dollar, "No cost, ever"], [Shield, "Insured both ways"], [CheckCircle, "Free to decline"]].map(([Glyph, label]) => (
        <li key={label} className="flex items-center gap-2 text-[12.5px] text-muted">
          <Glyph size={15} className="text-bronze shrink-0" />
          {label}
        </li>
      ))}
    </ul>
  );
}

////////// 8. SUCCESS //////////

////////// 9. PAGE //////////

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

const validators = {
  firstName: (value) => (String(value || "").trim() ? null : "Tell us your first name"),
  lastName: (value) => (String(value || "").trim() ? null : "Tell us your last name"),
  email: (value) => (EMAIL_RE.test(String(value || "").trim()) ? null : "Check this email address"),
  phone: (value) => (String(value || "").replace(/\D/g, "").length >= 10 ? null : "Use a 10 digit US number"),
  "address.postal": (value) => (/^\d{5}(-\d{4})?$/.test(String(value || "").trim()) ? null : "Use a 5 digit ZIP"),
  "address.line1": (value) => (String(value || "").trim() ? null : "We need a street address"),
  "address.city": (value) => (String(value || "").trim() ? null : "We need a city"),
  "address.state": (value) => (String(value || "").trim() ? null : "Pick a state"),
  acceptedTerms: (value) => (value === true ? null : "Please accept the terms to continue"),
};

const STEP_FIELDS = [
  [],
  ["firstName", "lastName", "email", "phone"],
  ["address.postal", "address.city", "address.state", "address.line1", "acceptedTerms"],
];

const EMPTY = {
  firstName: "", lastName: "", email: "", phone: "",
  address: { line1: "", line2: "", city: "", state: "", postal: "", country: "US" },
  // Every consent starts off. A ticked box the customer did not tick is not
  // consent, it is an assumption we would be storing under their name, and the
  // agreement record we keep is worth exactly what the tick was worth.
  smsConsent: false, marketingConsent: false, acceptedTerms: false, website: "",
};

// The answer to filling this form in twice.
//
// A second request is refused by the server rather than created, because two
// orders for one person means two mailers, two prepaid return labels and a real
// chance of gold arriving on the label belonging to the other one. That refusal
// is a bad thing to show as a red line under a text box: somebody who has just
// typed their address for the second time needs to be told they are already in
// the system and where to look, not told they made a mistake.
//
// It carries no tracking link. Whoever typed that email might not be the person
// who owns the order, so the way through is /track, which proves the phone or
// the email first. That is the same rule the recovery page has always had.

function AlreadyHaveIt({ duplicate, onClose }) {
  if (!duplicate) return null;
  return (
    <Modal
      open
      size="lg"
      onClose={onClose}
      title="We already have your request."
      description={`Order ${duplicate.number}, asked for on ${day(duplicate.requestedAt)}. Nothing new is needed from you.`}
      footer={(
        <>
          <Button variant="quiet" onClick={onClose}>Back to the form</Button>
          <Link to="/track" className="btn btn-primary">Find my order <ArrowRight size={16} /></Link>
        </>
      )}
    >
      <div className="pb-5 mb-5 border-b border-rule-soft">
        <p className="eyebrow mb-1.5">Where it is right now</p>
        <p className="display display-sm text-ink">{duplicate.statusLabel}</p>
      </div>

      <p className="text-[14.5px] leading-relaxed text-muted">
        We email and text at every step, so there is nothing to check up on and nothing to chase.
        Sending the form again would only put a second mailer in the post.
      </p>

      <WhatHappensNext stage={duplicate.stage} className="mt-6" />

      <div className="mt-7 pt-5 border-t border-rule-soft space-y-2">
        <p className="text-[13.5px] font-semibold text-ink">Have something else to send?</p>
        <p className="hint leading-relaxed">
          Reply to any email from us, or call{" "}
          <a href="tel:+1-401-315-4653" className="link text-ink font-semibold">(401) 315-4653</a>,
          and we will add it to this order or open a second one properly. Do not put a second lot in
          the same mailer without telling us first: everything that arrives in one envelope is
          weighed and quoted as one lot.
        </p>
      </div>
    </Modal>
  );
}

const valueAt = (values, name) => name.split(".").reduce((cursor, key) => cursor?.[key], values);

export default function RequestKit({ mailer = false }) {
  const navigate = useNavigate();
  const saved = useRef(readDraft()).current;

  const [step, setStep] = useState(saved?.step ?? 0);
  const [chips, setChips] = useState(saved?.chips ?? []);
  const [items, setItems] = useState(saved?.items ?? []);
  const [resumed, setResumed] = useState(Boolean(saved));
  // Set when the server refuses because this person already has an order.
  const [duplicate, setDuplicate] = useState(null);

  const draftId = useRef(saved?.draftId || newDraftId()).current;
  const form = useForm(saved?.values ?? EMPTY);
  const clearDraft = useDraft({ values: form.values, items, chips, step, draftId });

  const formName = mailer ? "have_a_mailer" : "kit_request";

  // Viewing is not starting, and most of the drop off is between the two.
  // form_view fires once on open; form_start waits for the first tile tapped or
  // field typed into, which is `began` below. `resumed` separates somebody
  // coming back to a saved draft from somebody arriving fresh.
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    analytics.trackFormView(formName, { resumed: Boolean(saved) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One capture handler on the form catches every field without threading a
  // callback through each one. The tiles are buttons and do not fire change, so
  // screen one calls it directly.
  const touched = useRef(false);
  const began = useCallback(() => {
    if (touched.current) return;
    touched.current = true;
    analytics.trackFormStart(formName, { resumed: Boolean(saved) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formName]);

  // Forward progress only, the same rule upsertDraft uses for `reached`:
  // somebody who reaches the address screen and clicks back to fix their email
  // has still reached the address screen, and should not be counted twice for
  // arriving at it again.
  const furthest = useRef(saved?.step ?? 0);

  // Send the partial to the server once there is a way to reach this person, and
  // again if they get further. `sent` keeps it to one call per screen rather than
  // one per keystroke.
  const sent = useRef("");
  const capture = useCallback((reached) => {
    const { firstName, lastName, email, phone, address, smsConsent, marketingConsent } = form.values;
    if (!email && !phone) return;
    // The postal code is in the fingerprint so that starting the address screen
    // and then leaving sends the fuller version, while tab switching does not
    // send the same thing over and over.
    const fingerprint = `${reached}:${email}:${phone}:${address?.postal || ""}`;
    if (sent.current === fingerprint) return;
    sent.current = fingerprint;

    publicApi.saveDraft({
      draftId,
      reached,
      deviceId: analytics.deviceId(),
      firstName, lastName, email, phone, address,
      chips,
      declared: items.filter((item) => item.description || item.approxWeight),
      smsConsent,
      marketingConsent,
      utm: readUtm(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values, chips, items, draftId]);

  // Leaving the tab from the last screen is the most common way to abandon, and
  // it is the only moment a normal effect will not catch. visibilitychange is the
  // one event that fires reliably on a phone; pagehide covers the rest.
  useEffect(() => {
    const onLeave = () => { if (step > 0) capture(step); };
    document.addEventListener("visibilitychange", onLeave);
    window.addEventListener("pagehide", onLeave);
    return () => {
      document.removeEventListener("visibilitychange", onLeave);
      window.removeEventListener("pagehide", onLeave);
    };
  }, [capture, step]);

  // Validate a field when it is left, not while it is being typed in.
  const touch = useCallback((name) => {
    const check = validators[name];
    if (!check) return;
    const message = check(valueAt(form.values, name));
    form.setErrors((previous) => ({ ...previous, [name]: message || undefined }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values]);

  // A server side failure on an earlier screen sends you back to fix it.
  useEffect(() => {
    const keys = Object.keys(form.errors).filter((key) => form.errors[key]);
    if (!keys.length) return;
    if (keys.some((key) => key.startsWith("address"))) setStep(2);
    else if (keys.some((key) => STEP_FIELDS[1].includes(key))) setStep(1);
  }, [form.errors]);

  const canContinue = STEP_FIELDS[step].every((name) => !validators[name](valueAt(form.values, name)));

  const next = () => {
    for (const name of STEP_FIELDS[step]) touch(name);
    if (!canContinue) return;
    // Leaving the contact screen is the first moment we can reach this person if
    // they never come back, so it is the moment the partial is kept.
    if (step >= 1) capture(step);
    const reached = Math.min(STEP_LABELS.length - 1, step + 1);
    if (reached > furthest.current) {
      furthest.current = reached;
      analytics.trackFormStep(formName, reached, STEP_LABELS[reached]);
    }
    setStep(reached);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = form.submit(async (values) => {
    // The last screen has no Continue button, so nothing else ever validates it.
    // Without this, an unticked agreement box means the button appears to do
    // nothing: the server refuses it and the reason never reaches the screen.
    const failures = {};
    for (const name of STEP_FIELDS[STEP_LABELS.length - 1]) {
      const message = validators[name]?.(valueAt(values, name));
      if (message) failures[name] = message;
    }
    if (Object.keys(failures).length) {
      form.setErrors((previous) => ({ ...previous, ...failures }));
      return false;
    }

    // `context()` is the campaign parameters this browser arrived with. It is
    // empty when the visitor has opted out, and the request works the same
    // either way.
    let response;
    try {
      response = await publicApi.requestKit({
        ...values,
        ...analytics.context(),
        draftId,
        // Outside the analytics spread on purpose. Whoever sent this customer is
        // owed their commission even when the customer has opted out of being
        // measured, so this is not allowed to travel with the tracking fields.
        ref: referralCode() || undefined,
        // Not a measurement either. It changes what happens next, and it is the
        // only thing that can match a parcel with no name on it to this form.
        mailer: mailer || undefined,
        card: (mailer && cardSerial()) || undefined,
        declared: items
          .filter((item) => item.description || item.approxWeight)
          .map((item) => ({
            ...item,
            quantity: Number(item.quantity) || 1,
            approxWeight: item.approxWeight === "" ? undefined : Number(item.approxWeight),
            karat: item.karat || undefined,
          })),
        // What they tapped on screen one. Vetting reads this, so a request with
        // no written detail still tells a reviewer what to expect.
        answers: {
          metals: chips.filter((value) => value !== "unsure").join(", "),
          unsure: chips.includes("unsure"),
          addedDetail: items.length > 0,
        },
      });
    } catch (problem) {
      // A refusal with an order attached is not a validation failure, so it does
      // not go back to useForm to be printed above the button. The draft is left
      // alone: they may want to correct an address and try a different email.
      if (problem.body?.duplicate) {
        setDuplicate(problem.body.duplicate);
        return false;
      }
      throw problem;
    }

    clearDraft();
    // The referral has done its job. Leaving it would credit a second order
    // months from now to a link nobody clicked that time.
    forgetReferral();
    // Same reasoning: a card is one piece of paper and it has now been used.
    forgetCard();
    // This browser now knows the order. The tracking page and the recovery page
    // both read it, so nobody who asked from this device has to find the email
    // again. See lib/session.js for why there is no account behind this.
    remember(response);
    // The visit becomes a profile, named by the order number and nothing else,
    // so the whole path from the first page view to here can be read as one
    // person. See analytics.js section 6.
    analytics.identify(response.number);
    // The confirmation is its own address, so an ad platform can count somebody
    // reaching it and a refresh lands somewhere that still makes sense. The event
    // is reported there rather than here, so the URL and the conversion agree.
    navigate("/get-a-kit/confirmed", { replace: true, state: { order: response } });
    return response;
  });

  // Enter moves forward rather than submitting a half filled form.
  const onKeyDown = (event) => {
    if (event.key !== "Enter" || event.target.tagName === "TEXTAREA") return;
    if (step < STEP_LABELS.length - 1) { event.preventDefault(); next(); }
  };

  const lastStep = step === STEP_LABELS.length - 1;
  const actionLabel = lastStep ? (form.busy ? "Sending" : "Send my free kit") : "Continue";

  const actionButton = (className) => (
    <Button
      type={lastStep ? "submit" : "button"}
      size="lg"
      onClick={lastStep ? undefined : next}
      loading={form.busy}
      className={className}
    >
      {actionLabel} {!lastStep && <ArrowRight size={16} />}
    </Button>
  );

  return (
    <>
      {mailer ? (
        <Seo
          title="Register the mailer you already have"
          path="/have-a-mailer"
          description="Already picked up one of our mailers? Tell us it is coming, and we will text you when it lands, weigh it on camera and have your offer the same day."
          noIndex
        />
      ) : (
        <Seo
          title="Get a free insured mail-in kit"
          path="/get-a-kit"
          description="Request a free, insured mail-in kit for gold, silver or platinum. No cost, no obligation, and free insured return shipping if you decline the offer."
        />
      )}
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Get a free kit", to: "/get-a-kit" }])} />

      <AlreadyHaveIt duplicate={duplicate} onClose={() => setDuplicate(null)} />

      <div className="shell pt-6 pb-5 sm:pt-8 sm:pb-6">
        <div className="mx-auto max-w-5xl grid gap-9 lg:gap-14 lg:grid-cols-[minmax(0,1fr)_18rem]">
          {/* Form */}
          <div className="min-w-0">
            <Enter as="p" from="none" className="eyebrow mb-3">
              {mailer ? "You already have the mailer" : "Free kit request"}
            </Enter>
            <Enter as="h1" delay={60} className="display display-md text-ink">
              {step === 0 && "What are you thinking of selling?"}
              {step === 1 && "How do we reach you?"}
              {step === 2 && (mailer ? "Where are you writing from?" : "Where should we send the kit?")}
            </Enter>
            <Enter as="p" delay={130} className="lede mt-2.5 sm:mt-3 text-muted measure">
              {step === 0 && "Tap whatever sounds right. None of this is required."}
              {step === 1 && "One email, and if you want them, texts at the two moments that matter."}
              {step === 2 && (mailer
                ? "Last screen. This is where your payment goes and where anything comes back to, not where we send a mailer."
                : "Last screen. A plain padded envelope.")}
            </Enter>

            {resumed && (
              <Alert tone="info" className="mt-5" onDismiss={() => setResumed(false)}>
                We picked up where you left off. Nothing has been sent yet.
              </Alert>
            )}

            <div className="mt-5 sm:mt-6">
              <Progress step={step} />

              <form onSubmit={submit} onKeyDown={onKeyDown} onChangeCapture={began} noValidate>
                {step === 0 && <StepWhat chips={chips} setChips={setChips} items={items} setItems={setItems} onBegin={began} formName={formName} />}
                {step === 1 && <StepContact form={form} touch={touch} />}
                {step === 2 && <StepAddress form={form} touch={touch} />}

                {/* Honeypot. Hidden from people, irresistible to scripts. */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute opacity-0 w-0 h-0 -z-10"
                  {...form.field("website")}
                />

                {form.message && <Alert tone="error" className="mt-5">{form.message.text}</Alert>}

                {/* Desktop actions. On a phone these are pinned to the bottom of
                    the screen instead, so the next step is never a scroll away. */}
                <div className="hidden sm:block mt-6">
                  <div className="flex items-center gap-3">
                    {step > 0 && (
                      <Button type="button" variant="quiet" onClick={() => setStep(step - 1)}>
                        <ArrowLeft size={16} /> Back
                      </Button>
                    )}
                    {actionButton()}
                    {step === 0 && chips.length === 0 && items.length === 0 && (
                      <span className="hint">Nothing selected is a fine answer.</span>
                    )}
                  </div>
                  <TrustRow className="mt-5 lg:hidden" />
                </div>

                {/* The first two sentences of this used to repeat the trust row
                    directly above it, word for word. Only the privacy link was
                    doing any work, so only the privacy link is left. */}
                {lastStep && (
                  <p className="hint mt-4">
                    <Link to="/legal/privacy" className="link text-ink">How we handle your details</Link>
                  </p>
                )}

                {/* Phone actions, pinned. */}
                <div className="sticky-cta sm:!hidden" data-shown="true">
                  <div className="flex items-center gap-2.5">
                    {step > 0 && (
                      <Button type="button" variant="quiet" onClick={() => setStep(step - 1)} aria-label="Back">
                        <ArrowLeft size={17} />
                      </Button>
                    )}
                    {actionButton("flex-1")}
                  </div>
                  <p className="text-[11.5px] text-center text-faint mt-1.5">
                    Free, insured both ways, and free to decline.
                  </p>
                </div>
                {/* Room so the pinned bar never covers the last field. */}
                <div className="h-28 sm:hidden" aria-hidden="true" />
              </form>
            </div>
          </div>

          {/* Reassurance rail. Sticky on desktop; on a phone it sits under the
              form, because the pinned bar already carries the short version. */}
          <aside className="lg:sticky lg:top-20 lg:self-start">
            <div className="panel p-5">
              {/* A photograph of the actual kit rather than a drawing of one.
                  This sits beside the form somebody is deciding whether to
                  fill in, so it is answering "what turns up" at the exact
                  moment the question is being asked. */}
              <Figure
                src={KIT.mailerHero}
                alt="The free appraisal mailer we send you"
                sizes="(min-width: 1024px) 20vw, 60vw"
                className="mb-4 max-w-[13rem] mx-auto"
              />
              <p className="eyebrow mb-3.5">What you are agreeing to</p>
              <ul className="space-y-3.5">
                {PROMISES.map(({ icon: Glyph, title, body }) => (
                  <li key={title} className="flex gap-2.5">
                    <Glyph size={16} className="text-bronze shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-[13.5px] text-ink">{title}</p>
                      <p className="hint mt-0.5 leading-snug">{body}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
